/* Issue ticket — Jira-like detail surface opened from the Admin desk › Issues tab.
   Workflow status, assignee, SLA timeline to fix, labels, and an account-based
   forum thread (events + comments) with a working composer.
   Reads window.MAV.{issueById, memberById, slaHours, now}. */

const { useState: useTktState, useRef: useTktRef, useEffect: useTktEffect } = React;

const WORKFLOW = [
  { id: 'triage',      label: 'Triage' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'in_review',   label: 'In review' },
  { id: 'resolved',    label: 'Resolved' },
];
const STATE_META = {
  triage:      { pill: 'pill-queued', label: 'Triage' },
  in_progress: { pill: 'pill-run',    label: 'In progress' },
  in_review:   { pill: 'pill-cond',   label: 'In review' },
  resolved:    { pill: 'pill-pass',   label: 'Resolved' },
};
const SEV_LABEL = { blocker: 'Blocker', high: 'High', medium: 'Medium', low: 'Low' };

function TAvatar({ m, size = 34 }) {
  if (!m) return <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.34, background: 'var(--queued)' }}>·</div>;
  return <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.34, background: m.color }}>{m.initials}</div>;
}

function fmtDate(iso) { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }

function slaInfo(issue, now) {
  const created = new Date(issue.created), due = new Date(issue.due);
  if (issue.state === 'resolved' && issue.resolvedOn) {
    return { resolved: true, pct: 100, text: 'Resolved ' + fmtDate(issue.resolvedOn), tone: 'ok' };
  }
  const total = due - created, elapsed = now - created;
  const pct = Math.max(2, Math.min(100, Math.round(elapsed / total * 100)));
  const msLeft = due - now, hLeft = msLeft / 36e5;
  let text, tone;
  if (msLeft < 0) { const d = Math.max(1, Math.ceil(-hLeft / 24)); text = 'Overdue by ' + d + 'd'; tone = 'over'; }
  else if (hLeft < 24) { text = 'Due in ' + Math.max(1, Math.round(hLeft)) + 'h'; tone = 'soon'; }
  else { text = Math.round(hLeft / 24) + 'd left'; tone = 'normal'; }
  return { resolved: false, pct, text, tone };
}

/* ---- right-rail field rows ---- */
function Field({ icon, label, children }) {
  return (
    <div className="tkt-field">
      <div className="tkt-field-k">{icon}{label}</div>
      <div className="tkt-field-v">{children}</div>
    </div>
  );
}

function AssigneePicker({ value, onChange }) {
  const [open, setOpen] = useTktState(false);
  const team = window.MAV.team;
  const m = window.MAV.memberById(value);
  return (
    <div className="tkt-assign">
      <button className="tkt-assign-cur" onClick={() => setOpen(o => !o)}>
        <TAvatar m={m} size={26} />
        <span className="nm" style={{ fontSize: 13, fontWeight: 600 }}>{m ? m.name : 'Unassigned'}</span>
        <I.chevron size={13} className="muted" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div className="tkt-assign-menu">
          {team.map(t => (
            <button key={t.id} className={'tkt-assign-opt' + (t.id === value ? ' sel' : '')} onClick={() => { onChange(t.id); setOpen(false); }}>
              <TAvatar m={t} size={24} />
              <span style={{ flex: 1, textAlign: 'left' }}>{t.name}</span>
              {t.id === value && <I.check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- activity thread ---- */
function EventRow({ ev }) {
  const m = ev.by === 'system' ? null : window.MAV.memberById(ev.by);
  const name = ev.by === 'system' ? 'Goose' : (m ? m.name : 'Someone');
  return (
    <div className="ev-row">
      <span className={'ev-dot' + (ev.by === 'system' ? ' system' : '')}>
        {ev.by === 'system' ? <I.shield size={11} /> : <TAvatar m={m} size={22} />}
      </span>
      <div className="ev-text"><b>{name}</b> {ev.action} <span className="ev-when">· {ev.when}</span></div>
    </div>
  );
}

function CommentRow({ c }) {
  const m = window.MAV.memberById(c.by);
  return (
    <div className="cmt">
      <TAvatar m={m} size={34} />
      <div className="cmt-body">
        <div className="cmt-head">
          <span className="cmt-name">{m ? m.name : c.by}</span>
          {m && <span className="cmt-role">{m.role}</span>}
          <span className="cmt-time">{c.when}</span>
        </div>
        <div className="cmt-text">{c.text}</div>
      </div>
    </div>
  );
}

function Composer({ onSend }) {
  const [val, setVal] = useTktState('');
  // The commenter is the signed-in user. Live `currentUser` is an object (from
  // hydrate's /me); the mock is the string 'jd'. Resolve to a team member when we
  // can (real avatar/role), else synthesize from the currentUser object.
  const cu = window.MAV.currentUser;
  const me = (cu && typeof cu === 'object')
    ? ((window.MAV.team || []).find((m) => (cu.ext_id && m.id === cu.ext_id) || (cu.id && m._id === cu.id) || (cu.name && m.name === cu.name))
        || { id: cu.ext_id || cu.id, name: cu.name || 'You', initials: cu.initials || String(cu.name || 'You').trim().slice(0, 2).toUpperCase(), color: 'var(--brand-orange)' })
    : (window.MAV.memberById(cu || 'jd') || window.MAV.memberById('jd') || { name: 'You', initials: 'YO', color: 'var(--brand-orange)' });
  const ref = useTktRef(null);
  const autosize = () => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 200) + 'px'; } };
  const submit = () => { const t = val.trim(); if (!t) return; onSend(t); setVal(''); requestAnimationFrame(autosize); };
  return (
    <div className="composer">
      <TAvatar m={me} size={34} />
      <div className="composer-box">
        <textarea ref={ref} value={val} rows={1} placeholder="Add a comment…  (⌘↵ to send)"
          onChange={e => { setVal(e.target.value); autosize(); }}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submit(); } }} />
        <div className="composer-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>Commenting as <b style={{ color: 'var(--ink-2)' }}>{me.name}</b></span>
          <button className="btn btn-primary" style={{ padding: '7px 15px', fontSize: 13 }} onClick={submit} disabled={!val.trim()}>
            <I.send size={14} /> Comment
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Issue ticket
   ============================================================ */
function IssueTicket({ issueId, back, go }) {
  const base = window.MAV.issueById(issueId);
  const [state, setState] = useTktState(base.state);
  const [assignee, setAssignee] = useTktState(base.assignee);
  const [thread, setThread] = useTktState(base.thread);
  const [watchers, setWatchers] = useTktState(base.watchers || []);
  const [addingWatcher, setAddingWatcher] = useTktState(false);
  const endRef = useTktRef(null);

  const reporter = window.MAV.memberById(base.reporter);
  const sla = slaInfo({ ...base, state }, window.MAV.now);
  const sm = STATE_META[state];

  // Live persistence (Jira-lite backend) with mock fallback: optimistic local
  // update, then PATCH/comment the server and reconcile from its canonical row.
  const meExt = (window.MAV.currentUser && window.MAV.currentUser.ext_id) || window.MAV.currentUser || 'jd';
  const live = !!(window.GooseAPI && window.GooseAPI.patchIssue);
  const reconcile = (iss) => {
    if (!iss) return;
    setState(iss.state); setAssignee(iss.assignee || null); setThread(iss.thread || []); setWatchers(iss.watchers || []);
    if (Array.isArray(window.MAV.issues)) { const i = window.MAV.issues.findIndex(x => x && x.id === iss.id); if (i >= 0) window.MAV.issues[i] = iss; }
  };
  const persist = (patch) => { if (live) window.GooseAPI.patchIssue(base.id, patch).then(r => reconcile(r && r.issue)).catch(() => {}); };

  const pushEvent = (action) => setThread(t => [...t, { kind: 'event', by: meExt, action, when: 'just now' }]);
  const sendComment = (text) => {
    setThread(t => [...t, { kind: 'comment', by: meExt, when: 'just now', text }]);
    requestAnimationFrame(() => endRef.current && endRef.current.scrollIntoView ? null : null);
    if (live) window.GooseAPI.commentIssue(base.id, text).then(r => reconcile(r && r.issue)).catch(() => {});
  };
  const changeState = (id) => { if (id === state) return; setState(id); pushEvent('changed status to ' + STATE_META[id].label); persist({ state: id }); };
  const changeAssignee = (id) => { if (id === assignee) return; setAssignee(id); const m = window.MAV.memberById(id); pushEvent('assigned the issue to ' + (m ? m.name : id)); persist({ assignee: id }); };
  const addWatcher = (id) => { if (watchers.includes(id)) return; const next = [...watchers, id]; setWatchers(next); setAddingWatcher(false); const m = window.MAV.memberById(id); pushEvent('added ' + (m ? m.name : id) + ' as a watcher'); persist({ watchers: next }); };
  const removeWatcher = (id) => { const next = watchers.filter(x => x !== id); setWatchers(next); const m = window.MAV.memberById(id); pushEvent('removed ' + (m ? m.name : id) + ' as a watcher'); persist({ watchers: next }); };
  const watchingMe = watchers.includes(meExt);
  const toggleSelfWatch = () => watchingMe ? removeWatcher(meExt) : addWatcher(meExt);

  const cmtCount = thread.filter(e => e.kind === 'comment').length;

  return (
    <div className="page tkt fade-in">
      <button className="tkt-back" onClick={back}><I.chevron size={15} style={{ transform: 'rotate(180deg)' }} /> Back to issues</button>

      {/* header */}
      <div className="tkt-headrow">
        <div style={{ minWidth: 0 }}>
          <div className="row gap-10 wrap" style={{ marginBottom: 10 }}>
            <span className={'tkt-type ' + (base.type === 'Bug' ? 'is-bug' : 'is-feat')}>
              {base.type === 'Bug' ? <I.flame size={12} /> : <I.star size={12} />}{base.type}
            </span>
            <span className="tkt-key">{base.id}</span>
            <span className={'ab-sevbadge sev-tag sev-' + base.sev}>{SEV_LABEL[base.sev]}</span>
          </div>
          <h1 className="tkt-title">{base.title}</h1>
        </div>
        <span className={'pill ' + sm.pill + ' tkt-statuspill'}>
          {state === 'in_progress' ? <span className="dot"></span> : state === 'resolved' ? <I.check size={12} /> : state === 'in_review' ? <I.warn size={12} /> : <span className="dot"></span>}
          {sm.label}
        </span>
      </div>

      {/* workflow */}
      <div className="tkt-workflow">
        {WORKFLOW.map((w, i) => {
          const idx = WORKFLOW.findIndex(x => x.id === state);
          const done = i < idx, cur = i === idx;
          return (
            <button key={w.id} className={'tkt-wf' + (done ? ' done' : '') + (cur ? ' cur' : '')} onClick={() => changeState(w.id)}>
              <span className="tkt-wf-dot">{done ? <I.check size={12} /> : i + 1}</span>
              {w.label}
            </button>
          );
        })}
      </div>

      <div className="tkt-grid">
        {/* main column */}
        <div className="tkt-main">
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title">Description</span></div>
            <p className="tkt-desc">{base.text}</p>
            <div className="tkt-meta-inline">
              <span><I.doc size={12} /> Reported from <button className="tkt-link" onClick={() => go && go('builds')}>{base.build}</button></span>
            </div>
          </div>

          <div className="tkt-activity">
            <div className="sec-head" style={{ marginBottom: 4 }}>
              <span className="sec-title"><I.chat size={15} style={{ verticalAlign: '-2px', marginRight: 7 }} />Activity</span>
              <span className="muted" style={{ fontSize: 12 }}>{cmtCount} comments · {thread.length - cmtCount} events</span>
            </div>
            <div className="thread">
              {thread.map((e, i) => e.kind === 'event' ? <EventRow key={i} ev={e} /> : <CommentRow key={i} c={e} />)}
              <div ref={endRef}></div>
            </div>
            <Composer onSend={sendComment} />
          </div>
        </div>

        {/* right rail */}
        <div className="tkt-rail">
          <div className="card card-pad tkt-fields">
            <Field icon={<I.users size={13} />} label="Assignee">
              <AssigneePicker value={assignee} onChange={changeAssignee} />
            </Field>
            <Field icon={<I.flag size={13} />} label="Reporter">
              <div className="row gap-8"><TAvatar m={reporter} size={26} /><span style={{ fontSize: 13, fontWeight: 600 }}>{reporter ? reporter.name : '—'}</span></div>
            </Field>
            <Field icon={<I.warn size={13} />} label="Severity">
              <span className={'ab-sevbadge sev-tag sev-' + base.sev}>{SEV_LABEL[base.sev]}</span>
            </Field>
            <Field icon={<I.calendar size={13} />} label="Created">
              <span className="tkt-fv-mono">{fmtDate(base.created)}</span>
            </Field>
            <Field icon={<I.link size={13} />} label="Linked build">
              <button className="tkt-link mono" onClick={() => go && go('builds')} style={{ fontSize: 12 }}>{base.build}</button>
            </Field>
          </div>

          {/* SLA timeline */}
          <div className="card card-pad tkt-sla">
            <div className="sec-head" style={{ marginBottom: 12 }}><span className="sec-title">Time to fix</span></div>
            <div className="row between" style={{ marginBottom: 8 }}>
              <span className="muted" style={{ fontSize: 11.5 }}>Opened {fmtDate(base.created)}</span>
              <span className={'tkt-sla-text ' + sla.tone}>{sla.text}</span>
            </div>
            <div className={'tkt-sla-bar tone-' + sla.tone}><span style={{ width: sla.pct + '%' }}></span></div>
            <div className="row between" style={{ marginTop: 8 }}>
              <span className="muted" style={{ fontSize: 11 }}>{SEV_LABEL[base.sev]} SLA · {Math.round(window.MAV.slaHours[base.sev] / 24) || 1}d target</span>
              <span className="muted mono" style={{ fontSize: 11 }}>Due {fmtDate(base.due)}</span>
            </div>
          </div>

          {/* labels */}
          <div className="card card-pad">
            <div className="sec-head" style={{ marginBottom: 12 }}><span className="sec-title">Labels</span></div>
            <div className="row wrap gap-8">
              {base.labels.map(l => <span key={l} className="tkt-label"><I.tag size={11} />{l}</span>)}
            </div>
          </div>

          {/* watchers */}
          <div className="card card-pad">
            <div className="sec-head" style={{ marginBottom: 12 }}>
              <span className="sec-title">Watchers</span>
              <button className="tkt-watch-toggle" onClick={toggleSelfWatch} title={watchingMe ? 'Stop watching this issue' : 'Watch this issue'}>
                <I.bell size={12} /> {watchingMe ? 'Watching' : 'Watch'}
              </button>
            </div>
            <div className="tkt-watchers">
              {watchers.map(w => {
                const m = window.MAV.memberById(w);
                return (
                  <button key={w} className="tkt-watcher" title={(m ? m.name : w) + ' · click to remove'} onClick={() => removeWatcher(w)}>
                    <TAvatar m={m} size={28} />
                    <span className="tkt-watcher-x"><I.x size={11} /></span>
                  </button>
                );
              })}
              <div className="tkt-watch-add-wrap">
                <button className="tkt-watch-add" onClick={() => setAddingWatcher(v => !v)} title="Add a watcher" aria-label="Add a watcher"><I.add size={15} /></button>
                {addingWatcher && (
                  <div className="tkt-watch-menu">
                    <div className="tkt-watch-menu-h">Add watcher</div>
                    {window.MAV.team.filter(m => !watchers.includes(m.id)).map(m => (
                      <button key={m.id} className="tkt-watch-opt" onClick={() => addWatcher(m.id)}>
                        <TAvatar m={m} size={22} /><span>{m.name}</span><span className="tkt-watch-opt-role">{m.role.split(' · ')[0]}</span>
                      </button>
                    ))}
                    {window.MAV.team.filter(m => !watchers.includes(m.id)).length === 0 && <div className="tkt-watch-empty">Everyone's already watching.</div>}
                  </div>
                )}
              </div>
            </div>
            {watchers.length === 0 && <p className="muted" style={{ fontSize: 11.5, margin: '10px 0 0' }}>No watchers yet — add teammates to keep them notified.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { IssueTicket });
